import { AxiosResponse } from 'axios';

import { useQueryClient } from '@tanstack/react-query';

import { PAY_STATEMENT_TYPE_CODE } from '../../constants';
import { TypePayStatementDetailMap, TypePayStatementType } from '../../types';
import { TypeRequestHeaders, axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

import { TypePayStatementDetailCommon } from './useGetPayStatementDetail';

type TypeGetPayStatementsByMonthParams = {
  date: string;
};

type TypeGetPayStatementsByMonthResponse = {
  code: string;
  message: string;
  data: TypePayStatementByMonth;
};

export type TypePayStatementByMonth = TypePayStatementDetailCommon & {
  /** 지급 월 기간 */
  payMonthPeriod: string;
  /** 총 근무일수 */
  totalWorkDay: number;
  /** 일급 금액 */
  totalDailyPay: number;
  /** 일급금액 발생 (일) */
  totalDailyCount: number;
  /** 월 보수 총 금액 */
  monthlyTotalPay: number;
  /** 공제항목 > 국민연금 */
  g001: number;
  /** 공제항목 > 건강보험 */
  g002: number;
  /** 공제항목 > 장기요양보험 */
  g003: number;
  /** 공제항목 > 총 금액 */
  total: number;
  /** 공제항목 > 공제완료 */
  gTotal: number;
  /** 공제항목 > 공제차액 */
  gTotalDifference: number;
};

export const isMonthlyDetail = (
  _detail: TypePayStatementDetailMap[TypePayStatementType],
  type: TypePayStatementType
): _detail is TypePayStatementByMonth => {
  return type === PAY_STATEMENT_TYPE_CODE.MONTHLY;
};

/** @description 월별 급여명세서 목록 조회 */
export const useGetPayStatementsByMonth = () => {
  const queryClient = useQueryClient();

  const requestGetPayStatementsByMonth = async ({
    params,
    headers,
    onSuccess,
    onError,
  }: {
    params: TypeGetPayStatementsByMonthParams;
    headers?: TypeRequestHeaders;
    onSuccess?: (response: TypeGetPayStatementsByMonthResponse) => void;
    onError?: (error: TypeErrorFormat) => void;
  }) => {
    const endpoint = '/labor/payslips/monthly';
    const query = queryClient.ensureQueryData<AxiosResponse<TypeGetPayStatementsByMonthResponse>>({
      queryKey: ['payStatementsByMonth', params.date],
      queryFn: () =>
        axiosInstance.get(`${endpoint}/${params.date}`, {
          headers,
        }),
      gcTime: 0,
    });

    return requestHelper(() => query, onSuccess, onError);
  };

  return requestGetPayStatementsByMonth;
};
